import {
  checkManagedTaskScreenOperation,
  type IManagedGuardContext,
  type ManagedTaskScreenRefusal
} from "../managed-task-screen-guard";
import { handleActionCategory } from "./handlers/cat-action";
import { handleActionExtendedCategory } from "./handlers/cat-action-extended";
import { handleBindCategory } from "./handlers/cat-bind";
import { handleComponentCategory } from "./handlers/cat-component";
import { handleHistoryCategory } from "./handlers/cat-history";
import { handleMicroappCategory } from "./handlers/cat-microapp";
import { handleProjectCategory } from "./handlers/cat-project";
import { handleProjectExtendedCategory } from "./handlers/cat-project-extended";
import { handlePropertyCategory } from "./handlers/cat-property";
import { handleScreenCategory } from "./handlers/cat-screen";
import { handleScreenInputCategory } from "./handlers/cat-screen-input";
// ── Category handlers (consolidated) ──
import { handleScreenQueryCategory } from "./handlers/cat-screen-query";
import { handleStyleCategory } from "./handlers/cat-style";
import { handleValidationCategory } from "./handlers/cat-validation";
import { handleWorkflowDslCategory } from "./handlers/cat-workflow-dsl";
import { resolveManagedGuardContext } from "./managed-guard-context";
import type { ToolInput } from "./types";

// ── Sync handlers (all-sync categories) ──
const syncHandlers: Record<string, (input: ToolInput) => any> = {
  ui_component: handleComponentCategory,
  ui_validation: handleValidationCategory,
  ui_action_extended: handleActionExtendedCategory,
  ui_history: handleHistoryCategory
};

// ── Async handlers (categories with any async operations) ──
const asyncHandlers: Record<string, (input: ToolInput) => Promise<any>> = {
  // KOA-18136 — `list_binding_sources` may fetch the workflow behind a
  // managed task screen; every other query operation stays sync underneath.
  ui_screen_query: handleScreenQueryCategory,
  ui_style: handleStyleCategory,
  ui_property: handlePropertyCategory,
  ui_bind: handleBindCategory,
  ui_screen: handleScreenCategory,
  ui_screen_input: handleScreenInputCategory,
  ui_project: handleProjectCategory,
  ui_project_extended: handleProjectExtendedCategory,
  ui_microapp: handleMicroappCategory,
  ui_action: handleActionCategory,
  ui_workflow_dsl: handleWorkflowDslCategory
};

/**
 * KOA-18136 — wizard-generated components on a managed workflow task screen may
 * be RESTYLED and nothing else.
 *
 * Enforced here, in front of the whole executor, because this is the single entry
 * point every MCP tool call reaches — including `ui_render` / `ui_edit_source` /
 * `ui_edit_patch`, which the server-side expander decomposes into `ui_component.*`
 * calls under the harness principal. A guard on individual category handlers only
 * ever protected the categories someone remembered to wrap, which is how
 * `ui_action` / `ui_property` / `ui_bind` stayed open and let ResumeProcess
 * wiring be damaged durably.
 *
 * Only the AI surface passes through here — the human editor mutates through the
 * designer providers directly — so refusing is never a locked editor.
 */
function refuseIfManagedTaskScreenViolation(toolName: string, input: ToolInput): ManagedTaskScreenRefusal | null {
  const operation: string = typeof input.operation === "string" ? input.operation : "";
  const ctx: IManagedGuardContext | null = resolveManagedGuardContext();
  return checkManagedTaskScreenOperation(ctx, toolName, operation, input);
}

export class McpToolExecutor {
  static execute(toolName: string, input: ToolInput): any | Promise<any> {
    const refusal: ManagedTaskScreenRefusal | null = refuseIfManagedTaskScreenViolation(toolName, input);
    if (refusal !== null) {
      return {
        success: false,
        error: refusal.error,
        ...(refusal.kuikaId !== null ? { kuikaId: refusal.kuikaId } : {}),
        managedTaskScreen: true
      };
    }

    const asyncHandler = asyncHandlers[toolName];
    if (asyncHandler) {
      return asyncHandler(input).catch((e: any) => {
        console.error(`[MCP] ${toolName} failed:`, e);
        return { error: e.message || `Failed to execute ${toolName}` };
      });
    }

    const handler = syncHandlers[toolName];
    if (!handler) {
      return { error: `Unknown tool: ${toolName}` };
    }

    try {
      return handler(input);
    } catch (e: any) {
      console.error(`[MCP] ${toolName} failed:`, e);
      return { error: e.message || `Failed to execute ${toolName}` };
    }
  }
}
